import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  FileText,
  Users,
  Upload,
  LogOut,
  Presentation,
  Settings
} from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

const Sidebar = () => {
  const { user, logout, canManageUsers } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'validator'] },
    { to: '/decisions', icon: FileText, label: 'Decision Library', roles: ['admin', 'editor', 'validator', 'viewer'] },
    { to: '/meeting-mode', icon: Presentation, label: 'Meeting Mode', roles: ['admin', 'editor', 'validator', 'viewer'] },
    { to: '/users', icon: Users, label: 'User Management', roles: ['admin'] },
    { to: '/import-export', icon: Upload, label: 'Import / Export', roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <aside className="sidebar flex flex-col" data-testid="sidebar">
      {/* Logo Area */}
      <div className="p-6 border-b border-white/10">
        <h1 className="text-xl font-bold text-white font-serif">NSR Workflow</h1>
        <p className="text-xs text-white/60 mt-1">Decision Log System</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `sidebar-item ${isActive ? 'active' : ''}`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <Separator className="bg-white/10" />

      {/* User Info */}
      <div className="p-4">
        <div className="mb-3">
          <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
          <p className="text-xs text-white/60 capitalize">{user?.role}</p>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>

      {/* Partner Logos */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center justify-center gap-3 opacity-70">
          <img 
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/UNICEF_Logo.svg/200px-UNICEF_Logo.svg.png" 
            alt="UNICEF" 
            className="h-6 object-contain brightness-0 invert"
          />
          <img 
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/87/World_Bank_logo.svg/200px-World_Bank_logo.svg.png" 
            alt="World Bank" 
            className="h-6 object-contain brightness-0 invert"
          />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
